import React, { useState } from "react";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { hasSubcategories, subcategoriesFor } from "../constants/subcategories";
import { variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { useMembership } from "../context/MembershipContext";
import Modal from "./Modal";
import ImagePicker from "./ImagePicker";
import JuiceDetailsFields, {
  DEFAULT_JUICE_DETAILS,
  parseJuiceDetails,
  serializeJuiceDetails,
} from "./JuiceDetailsFields";

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

/**
 * A range: the shared face of a model or brand line. What is typed here is
 * copied into each variant when it is created — never read back live — so
 * the fields are the ones a variant starts from, not a template it inherits.
 */
const RangeForm = ({ group, onClose, onSaved }) => {
  const { t } = useI18n();
  const { can } = useMembership();
  const seesCosts = can("power.see_costs");
  const currency = useShopCurrency();
  const [form, setForm] = useState({
    name: group?.name || "",
    category: group?.category || "puff",
    subcategory: group?.subcategory || "",
    brand: group?.brand || "",
    price: group?.price ?? "",
    cost_price: group?.cost_price ?? "",
  });
  const [juiceDetails, setJuiceDetails] = useState(
    group?.juice_details
      ? parseJuiceDetails(group.juice_details)
      : DEFAULT_JUICE_DETAILS,
  );
  const [existingImages, setExistingImages] = useState(group?.images || []);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const body = new FormData();
      body.append("name", form.name.trim());
      body.append("category", form.category);
      body.append("subcategory", form.subcategory || "");
      body.append("brand", form.brand || "");
      body.append("price", parseFloat(form.price) || 0);

      // Left empty means "not known yet", so a new range sends nothing; an
      // edit sends "" so a buy price can be cleared as well as changed.
      if (form.cost_price !== "" && form.cost_price !== null) {
        body.append("cost_price", parseFloat(form.cost_price));
      } else if (group) {
        body.append("cost_price", "");
      }

      if (form.category === "juice") {
        body.append(
          "juice_details",
          JSON.stringify(serializeJuiceDetails(juiceDetails)),
        );
      }

      files.forEach((file) => body.append("images", file));
      if (group) body.append("existingImages", JSON.stringify(existingImages));

      const { data } = group
        ? await variantGroupsAPI.update(group.id, body)
        : await variantGroupsAPI.create(body);

      onSaved(data.group);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("ranges.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={group ? t("ranges.form.editTitle") : t("ranges.form.newTitle")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t("ranges.form.name")}</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder={t("ranges.form.namePlaceholder")}
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("ranges.form.nameHint")}
            </p>
          </div>

          <div>
            <label className={labelClass}>{t("products.form.brand")}</label>
            <input
              type="text"
              value={form.brand}
              onChange={(e) => set({ brand: e.target.value })}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t("products.form.category")}</label>
            <select
              value={form.category}
              onChange={(e) => set({ category: e.target.value, subcategory: "" })}
              className={inputClass}
            >
              {PRODUCT_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {hasSubcategories(form.category) && (
            <div>
              <label className={labelClass}>
                {t("products.form.subcategory")}
              </label>
              <select
                value={form.subcategory}
                onChange={(e) => set({ subcategory: e.target.value })}
                className={inputClass}
              >
                <option value="">{t("products.form.noSubcategory")}</option>
                {subcategoriesFor(form.category).map((subcategory) => (
                  <option key={subcategory.value} value={subcategory.value}>
                    {subcategory.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className={labelClass}>
              {t("products.form.price", { currency })}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              required
              value={form.price}
              onChange={(e) => set({ price: e.target.value })}
              className={inputClass}
            />
          </div>

          {seesCosts && (
            <div>
              <label className={labelClass}>
                {t("products.form.buyPrice", { currency })}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder={t("products.form.buyPricePlaceholder")}
                value={form.cost_price}
                onChange={(e) => set({ cost_price: e.target.value })}
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("products.form.costPrivate")}
              </p>
            </div>
          )}
        </div>

        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
          {t("ranges.form.copyNote")}
        </p>

        <ImagePicker
          existing={existingImages}
          onExistingChange={setExistingImages}
          onFilesChange={setFiles}
          editing={Boolean(group)}
        />

        {form.category === "juice" && (
          <JuiceDetailsFields value={juiceDetails} onChange={setJuiceDetails} />
        )}

        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-all"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
          >
            {saving ? t("ranges.form.saving") : t("ranges.form.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RangeForm;
